import { SEO } from "@/components/seo/SEO";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";

export default function CookiePolicy() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Cookie Policy - SOL-VI",
    "description": "Cookie policy van SOL-VI - uitleg over het gebruik van cookies op onze website",
    "publisher": {
      "@type": "Organization",
      "name": "SOL-VI",
      "url": "https://sol-vi.nl"
    }
  };

  return (
    <>
      <SEO 
        title="Cookie Policy - SOL-VI | Gebruik van Cookies"
        description="Cookie policy van SOL-VI - uitleg over het gebruik van cookies op onze website voor een betere gebruikerservaring."
        canonical="https://sol-vi.nl/cookie-policy"
        jsonLd={[structuredData]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl font-bold mb-8">Cookie Policy</h1>
            
            <div className="prose prose-gray max-w-none">
              <p className="text-lg text-muted-foreground mb-8">
                Laatst bijgewerkt: 25 augustus 2024
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">1. Wat zijn cookies?</h2>
                <p>
                  Cookies zijn kleine tekstbestanden die op uw apparaat worden opgeslagen wanneer u onze website bezoekt. 
                  Ze helpen ons de website te verbeteren en u een betere ervaring te bieden.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">2. Welke cookies gebruiken wij?</h2>
                
                <div className="space-y-6">
                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Noodzakelijke Cookies</h3>
                    <p className="text-sm text-muted-foreground mb-2">Deze cookies zijn essentieel voor het functioneren van de website.</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Sessiebeheer</li>
                      <li>Cookie voorkeuren</li>
                      <li>Beveiligingsinstellingen</li>
                    </ul>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Analytische Cookies</h3>
                    <p className="text-sm text-muted-foreground mb-2">Deze cookies helpen ons begrijpen hoe bezoekers de website gebruiken.</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Google Analytics (GA4)</li>
                      <li>Plausible Analytics</li>
                      <li>Gebruiksstatistieken</li>
                    </ul>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Functionele Cookies</h3>
                    <p className="text-sm text-muted-foreground mb-2">Deze cookies verbeteren de functionaliteit van de website.</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Calculator instellingen</li>
                      <li>Formulier opslag</li>
                      <li>Taalvoorkeuren</li>
                    </ul>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Marketing Cookies</h3>
                    <p className="text-sm text-muted-foreground mb-2">Deze cookies worden gebruikt voor gerichte advertenties (alleen met uw toestemming).</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li>Google Ads conversie tracking</li>
                      <li>Social media pixels</li>
                      <li>Remarketing tags</li>
                      <li><strong>Daisycon affiliate tracking</strong> - Voor commissie tracking van affiliate partnerships</li>
                      <li>Leadinfo B2B tracking</li>
                    </ul>
                  </div>

                  <div className="bg-muted p-4 rounded-lg">
                    <h3 className="text-lg font-semibold mb-2">Affiliate Marketing Cookies</h3>
                    <p className="text-sm text-muted-foreground mb-2">Specifieke cookies voor affiliate partnerships en commissie tracking.</p>
                    <ul className="list-disc list-inside space-y-1 text-sm">
                      <li><strong>Daisycon tracking cookies</strong> - Voor bijhouden van affiliate conversies en commissies</li>
                      <li><strong>Partner platform cookies</strong> - Voor analyse van affiliate prestaties</li>
                      <li><strong>Referral tracking</strong> - Voor correct toekennen van commissies aan partners</li>
                    </ul>
                    <p className="text-xs text-muted-foreground mt-2">
                      Deze cookies bevatten geen persoonlijke informatie en worden gebruikt voor transparante commissie-administratie conform Daisycon voorwaarden.
                    </p>
                  </div>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">3. Google Analytics en Privacy</h2>
                <p>
                  Wij gebruiken Google Analytics 4 (GA4) met Consent Mode v2 om uw privacy te respecteren. 
                  Dit betekent dat:
                </p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Tracking alleen plaatsvindt met uw toestemming</li>
                  <li>IP-adressen worden geanonimiseerd</li>
                  <li>Gegevens worden beperkt bewaard</li>
                  <li>U kunt uw toestemming intrekken</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">4. Uw keuzes</h2>
                <p>U kunt uw cookie voorkeuren op elk moment aanpassen:</p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li>Via de cookie banner op onze website</li>
                  <li>Door uw browserinstellingen aan te passen</li>
                  <li>Door contact met ons op te nemen</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">5. Cookies van derden</h2>
                <p>
                  Sommige cookies worden geplaatst door externe services die wij gebruiken:
                </p>
                <ul className="list-disc list-inside space-y-2 mt-2">
                  <li><strong>Google Analytics:</strong> Voor website statistieken</li>
                  <li><strong>Google Maps:</strong> Voor kaarten en locatieservices</li>
                  <li><strong>YouTube:</strong> Voor ingesloten video's</li>
                  <li><strong>OpenSolar:</strong> Voor 3D dakontwerp functionaliteit</li>
                  <li><strong>Daisycon:</strong> Voor affiliate marketing tracking en commissie-administratie</li>
                  <li><strong>Leadinfo:</strong> Voor B2B bezoekersanalyse en lead tracking</li>
                </ul>
                
                <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mt-4">
                  <p className="text-sm">
                    <strong>Daisycon Partnership:</strong> Wij werken samen met Daisycon voor affiliate marketing. 
                    Hun tracking cookies helpen ons bij het correct toekennen van commissies aan onze partners. 
                    Deze cookies bevatten geen persoonlijke informatie en worden alleen geplaatst met uw toestemming. 
                    Meer informatie vindt u in de{' '}
                    <a href="https://www.daisycon.com/nl/standaard-verwerkersovereenkomst-publishers/" 
                       target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      Daisycon verwerkersovereenkomst
                    </a>.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">6. Bewaarperiode</h2>
                <div className="bg-muted p-4 rounded-lg">
                  <ul className="space-y-2 text-sm">
                    <li><strong>Sessie cookies:</strong> Worden verwijderd wanneer u de browser sluit</li>
                    <li><strong>Functionele cookies:</strong> Maximaal 1 jaar</li>
                    <li><strong>Analytische cookies:</strong> Maximaal 26 maanden</li>
                    <li><strong>Marketing cookies:</strong> Variabel, maximaal 2 jaar</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold mb-4">7. Contact</h2>
                <p>
                  Voor vragen over cookies of deze policy kunt u contact opnemen:
                </p>
                <div className="bg-muted p-4 rounded-lg mt-4">
                  <p><strong>SOL-VI</strong></p>
                  <p>E-mail: <a href="mailto:info@sol-vi.nl" className="text-primary hover:underline">info@sol-vi.nl</a></p>
                  <p>Telefoon: 06 17 10 57 86</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </>
  );
}